import { useEffect, useRef, useState } from "react";
import { TooltipV2, TooltipV2Props } from "~components";
import { Properties } from "~generated/css";
import { useTestIds } from "~utils";

type OverflowTooltipProps = {
  text: string;
  id: string;
  placement?: TooltipV2Props["place"];
  xss?: Properties;
  width?: string;
};

// Renders `text` clipped by overflow rules in `xss` (e.g. truncate or lineClamp),
// and only shows a tooltip with the full text when the rendered content is actually clipped.
export function OverflowTooltip(props: OverflowTooltipProps) {
  const { text, id, placement = "top", xss = {}, width = "280px" } = props;
  const tid = useTestIds(props, "overflow-tooltip");
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const anchorId = `overflow-tooltip-${id.replace(":", "")}`;

  useEffect(() => {
    const el = textRef.current;
    if (!el) return; // Bail if it's nulled out
    const updateOverflow = () => setIsOverflowing(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(el);
    updateOverflow();
    return () => resizeObserver.disconnect();
  }, [text]);

  return (
    <>
      <span id={anchorId} ref={textRef} css={{ ...xss }} {...tid}>
        {text}
      </span>
      <TooltipV2
        positionStrategy="fixed"
        anchorSelect={`#${anchorId}`}
        place={placement}
        width={width}
        hidden={!isOverflowing}
      >
        {text}
      </TooltipV2>
    </>
  );
}
